/**
 * AD-007b's gating table, as data. There is one physical model; the FLIGHT
 * MODEL tier and the Realism Panel toggles switch whole subsystems on and off.
 * Every row below is a line of that table, and the integrator asks this module
 * rather than testing the tier inline — the design note is explicit that
 * `if (tier === ...)` must not be scattered through the physics.
 */

/** Manual, Realism Panel: FLIGHT MODEL is a 3-way setting, not a difficulty knob. */
export const FLIGHT_MODEL_TIERS = ["novice", "intermediate", "expert"] as const;

export type FlightModelTier = (typeof FLIGHT_MODEL_TIERS)[number];

/**
 * The Realism Panel toggles the sim core actually consumes. The remaining four
 * (REALISTIC INSTRUMENTS, SUN BLIND SPOT, REAL NAVIGATION, and the OVERALL
 * DIFFICULTY readout) are presentation/scoring concerns and belong to the UI and
 * campaign lanes.
 *
 * REALISTIC WEATHER belongs to the sim rather than to presentation, because
 * `RB.EXE`'s flag for it is what decides whether a wind model is installed at
 * all (AD-007k). The Realism Panel's option table is a 13-entry array of far
 * strings and the flags are the parallel 13-word block at `ds:0x7267`, with
 * REALISTIC WEATHER at index 2 (`ds:0x726b`), GUN JAMS ALLOWED at index 3
 * (`ds:0x726d`, read by `mg.c`) and COMBAT LEVEL at index 12 (`ds:0x727f`, the
 * address AD-010 §9.4 found independently) — which is what pins the reading.
 */
export interface RealismSettings {
  readonly gunJams: boolean;
  readonly blackouts: boolean;
  readonly carburetorFreezes: boolean;
  readonly limitedFuel: boolean;
  readonly limitedAmmunition: boolean;
  readonly aircraftMayBeDamaged: boolean;
  /** Reserved: the collision responder belongs to the AI/collision lane, which owns other aircraft. */
  readonly midairCollisions: boolean;
  /** REALISTIC WEATHER. Off means no wind at all — the original installs no wind model (AD-007k). */
  readonly realisticWeather: boolean;
}

export type SubsystemId =
  | "bankToTurnAssist"
  | "coordinatedTurn"
  | "spiralDive"
  | "spin"
  | "rotaryTorque"
  | "overstressFailure"
  | "componentDamage"
  | "gunJams"
  | "hypoxia"
  | "carburettorIce"
  | "limitedFuel"
  | "limitedAmmunition"
  | "midairCollisions"
  | "wind";

export interface SubsystemRule {
  readonly id: SubsystemId;
  readonly enabled: (tier: FlightModelTier, realism: RealismSettings) => boolean;
  /** The AD-007b row (or manual line) this rule encodes, for the table-fidelity test. */
  readonly source: string;
}

export const SUBSYSTEM_RULES: readonly SubsystemRule[] = [
  {
    id: "bankToTurnAssist",
    enabled: (tier) => tier === "novice",
    source: 'Novice: "If you bank the aircraft, it will turn. Your aircraft will not nose down in a turn."',
  },
  {
    id: "coordinatedTurn",
    enabled: (tier) => tier !== "novice",
    source: "AD-007b: coordinated-turn requirement on at Intermediate and Expert.",
  },
  {
    id: "spiralDive",
    enabled: (tier) => tier !== "novice",
    source: 'Intermediate: "your turn will quickly degenerate into a slow spiral dive."',
  },
  {
    id: "spin",
    enabled: (tier) => tier === "expert",
    source: 'Expert: "the danger of going into a spin is present."',
  },
  {
    id: "rotaryTorque",
    enabled: (tier) => tier === "expert",
    source: 'Expert: "the gyroscopic effect of the Sopwith Camel\'s rotary engine..."',
  },
  {
    id: "overstressFailure",
    enabled: (tier, realism) => tier === "expert" && realism.aircraftMayBeDamaged,
    source: 'Expert + AIRCRAFT MAY BE DAMAGED: "if too much stress is put upon your aircraft\'s wings during a high speed dive, your wings may break!"',
  },
  {
    id: "componentDamage",
    enabled: (_tier, realism) => realism.aircraftMayBeDamaged,
    source: "AIRCRAFT MAY BE DAMAGED: off = the aircraft is invulnerable.",
  },
  { id: "gunJams", enabled: (_tier, realism) => realism.gunJams, source: "GUN JAMS ALLOWED." },
  { id: "hypoxia", enabled: (_tier, realism) => realism.blackouts, source: "BLACKOUTS ALLOWED (hypoxia above ~20,000 ft, not g-force)." },
  { id: "carburettorIce", enabled: (_tier, realism) => realism.carburetorFreezes, source: "CARBURETOR FREEZES." },
  { id: "limitedFuel", enabled: (_tier, realism) => realism.limitedFuel, source: "LIMITED FUEL." },
  { id: "limitedAmmunition", enabled: (_tier, realism) => realism.limitedAmmunition, source: "LIMITED AMMUNITION." },
  { id: "midairCollisions", enabled: (_tier, realism) => realism.midairCollisions, source: "MIDAIR COLLISIONS." },
  {
    id: "wind",
    enabled: (_tier, realism) => realism.realisticWeather,
    source:
      "REALISTIC WEATHER: `ds:0x726b` is what selects a wind model in `RB.EXE`; with it off the compiled record is never installed and there is no wind (AD-007k).",
  },
];

export interface SubsystemFlags {
  readonly bankToTurnAssist: boolean;
  readonly coordinatedTurn: boolean;
  readonly spiralDive: boolean;
  readonly spin: boolean;
  readonly rotaryTorque: boolean;
  readonly overstressFailure: boolean;
  readonly componentDamage: boolean;
  readonly gunJams: boolean;
  readonly hypoxia: boolean;
  readonly carburettorIce: boolean;
  readonly limitedFuel: boolean;
  readonly limitedAmmunition: boolean;
  readonly midairCollisions: boolean;
  readonly wind: boolean;
}

export function subsystemEnabled(
  id: SubsystemId,
  tier: FlightModelTier,
  realism: RealismSettings,
): boolean {
  const rule = SUBSYSTEM_RULES.find((candidate) => candidate.id === id);
  if (rule === undefined) throw new Error(`unknown flight subsystem: ${id}`);
  return rule.enabled(tier, realism);
}

/** Evaluates the whole table once per tick so no physics code re-reads the tier. */
export function resolveSubsystems(tier: FlightModelTier, realism: RealismSettings): SubsystemFlags {
  return {
    bankToTurnAssist: subsystemEnabled("bankToTurnAssist", tier, realism),
    coordinatedTurn: subsystemEnabled("coordinatedTurn", tier, realism),
    spiralDive: subsystemEnabled("spiralDive", tier, realism),
    spin: subsystemEnabled("spin", tier, realism),
    rotaryTorque: subsystemEnabled("rotaryTorque", tier, realism),
    overstressFailure: subsystemEnabled("overstressFailure", tier, realism),
    componentDamage: subsystemEnabled("componentDamage", tier, realism),
    gunJams: subsystemEnabled("gunJams", tier, realism),
    hypoxia: subsystemEnabled("hypoxia", tier, realism),
    carburettorIce: subsystemEnabled("carburettorIce", tier, realism),
    limitedFuel: subsystemEnabled("limitedFuel", tier, realism),
    limitedAmmunition: subsystemEnabled("limitedAmmunition", tier, realism),
    midairCollisions: subsystemEnabled("midairCollisions", tier, realism),
    wind: subsystemEnabled("wind", tier, realism),
  };
}

export const ALL_REALISM_OFF: RealismSettings = {
  gunJams: false,
  blackouts: false,
  carburetorFreezes: false,
  limitedFuel: false,
  limitedAmmunition: false,
  aircraftMayBeDamaged: false,
  midairCollisions: false,
  realisticWeather: false,
};

export const ALL_REALISM_ON: RealismSettings = {
  gunJams: true,
  blackouts: true,
  carburetorFreezes: true,
  limitedFuel: true,
  limitedAmmunition: true,
  aircraftMayBeDamaged: true,
  midairCollisions: true,
  realisticWeather: true,
};

/**
 * The manual's three one-press preset buttons ("each has default settings for
 * different skill levels including Novice, Intermediate, and Expert") without
 * itemizing them — so the middle row is INFERRED: the settings that change how
 * hard the *aircraft* is to keep flying stay off, the ones that limit resources
 * come on.
 *
 * `realisticWeather` is the one entry in the middle row that is not inferred.
 * Both realism preference files the game ships — `CREAL.PRF` (campaign) and
 * `MREAL.PRF` (single mission), 26 bytes each, i.e. the 13 option words — carry
 * `1` at index 2, so REALISTIC WEATHER is on out of the box in the original.
 */
export const REALISM_PRESETS: Readonly<Record<FlightModelTier, RealismSettings>> = {
  novice: ALL_REALISM_OFF,
  intermediate: {
    gunJams: false,
    blackouts: false,
    carburetorFreezes: false,
    limitedFuel: true,
    limitedAmmunition: true,
    aircraftMayBeDamaged: true,
    midairCollisions: true,
    realisticWeather: true,
  },
  expert: ALL_REALISM_ON,
};

export function makeRealism(overrides: Partial<RealismSettings> = {}): RealismSettings {
  return { ...ALL_REALISM_ON, ...overrides };
}
